import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Container,
  Button,
  ListGroup,
  Alert,
} from "react-bootstrap";
import {
  UserPlus,
  DollarSign,
  FileText,
  Smartphone,
  HelpCircle,
  CheckCircle,
  ArrowRight,
} from "react-feather";
import Swal from "sweetalert2";

import UtiPanCardService from "../../../services/UtiPanCardService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";

const AgentRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);

  const agentOnboardHandler = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const result = await UtiPanCardService.agentOnboard();

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setResponseData(res.data);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.message || "Registration successful!",
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: DollarSign, text: "Earn commission on every PAN card processed" },
    { icon: DollarSign, text: "Instant commission credited to wallet" },
    { icon: FileText, text: "Zero investment, 100% paperless process" },
    { icon: Smartphone, text: "Work anytime, anywhere using your phone" },
    { icon: HelpCircle, text: "Get training & support from UTI PAN team" },
  ];

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-4">
              {/* Header */}
              <div className="text-center mb-5">
                <div className="bg-primary bg-gradient p-3 rounded-circle d-inline-flex mb-3">
                  <UserPlus size={40} color="white" />
                </div>
                <h2 className="mb-3">Become a UTI PAN Agent</h2>
                <p className="text-muted fs-5 mb-4">
                  Start earning by helping clients apply for PAN cards
                </p>
              </div>

              {/* Success Message */}
              {responseData && (
                <Alert
                  variant="success"
                  className="d-flex align-items-center mb-4"
                >
                  <CheckCircle size={24} className="me-3" />
                  <div>
                    <h5 className="mb-1">Registration Successful!</h5>
                    <p className="mb-0">Your agent account has been created.</p>
                  </div>
                </Alert>
              )}

              <Row>
                {/* Left Column - Content */}
                <Col lg={7} className="mb-4 mb-lg-0">
                  <div className="pe-lg-4">
                    <h4 className="mb-4">Start Your PAN Agent Journey</h4>
                    <p className="text-muted mb-4">
                      Join as a certified UTI PAN Agent and earn instant
                      commission on each successful PAN service. Work from
                      anywhere with our digital platform.
                    </p>

                    <div className="mb-4">
                      <h5 className="mb-3">Why Join Us?</h5>
                      <ListGroup variant="flush">
                        {benefits.map((item, index) => (
                          <ListGroup.Item
                            key={index}
                            className="border-0 px-0 py-2"
                          >
                            <div className="d-flex align-items-start">
                              <div className="bg-light p-2 rounded-circle me-3">
                                <item.icon size={18} className="text-primary" />
                              </div>
                              <span>{item.text}</span>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>

                    <Button
                      variant="primary"
                      size="lg"
                      onClick={agentOnboardHandler}
                      disabled={loading}
                      className="px-4"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <UserPlus size={20} className="me-2" />
                          Register Now
                          <ArrowRight size={20} className="ms-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </Col>

                {/* Right Column - Features */}
                <Col lg={5}>
                  <Card className="border-0 bg-light h-100">
                    <Card.Body className="p-4">
                      <h5 className="mb-4">Quick Overview</h5>

                      <div className="mb-3">
                        <h6 className="text-primary mb-2">
                          No Investment Required
                        </h6>
                        <p className="small text-muted mb-0">
                          Start immediately without any security deposits
                        </p>
                      </div>

                      <div className="mb-3">
                        <h6 className="text-primary mb-2">Digital Process</h6>
                        <p className="small text-muted mb-0">
                          Everything handled online - no paperwork needed
                        </p>
                      </div>

                      <div className="mb-3">
                        <h6 className="text-primary mb-2">24/7 Dashboard</h6>
                        <p className="small text-muted mb-0">
                          Track earnings and applications anytime
                        </p>
                      </div>

                      <div className="mt-4 pt-3 border-top">
                        <p className="small text-muted mb-2">Powered by</p>
                        <div className="bg-white p-2 rounded d-inline-block">
                          <img src="/assets/images/uti_logo.png" width={50} />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AgentRegistration;
